import { ROOT_CATEGORY_ID } from "../../normalizers/constants";
import { normalizeQuantity } from "../../normalizers/normalizeQuantity";

function getCategoryId(category) {
    return category?.id == null
        ? ROOT_CATEGORY_ID
        : category.id;
}

function createCategoryNode(category) {
    const isRoot = category.id == null;

    return {
        id: category.id,
        nodeId: isRoot ? ROOT_CATEGORY_ID : `c${category.id}`,
        code: category.code,
        title: category.title || (isRoot ? "Root" : "Uncategorized"),
        type: "category",
        orderNumber: category.order_number ?? 9999,
        parents: [],
        direct: [],
    };
}

export function ensureCategory(state, category) {
    const categoryId = getCategoryId(category);

    let categoriesById = { ...state.categoriesById };
    let categoryIds = [...state.categoryIds];

    if (!categoriesById[categoryId]) {
        categoriesById[categoryId] = createCategoryNode(category);

        categoryIds.push(categoryId);

        categoryIds.sort((a, b) => {
            if (a === ROOT_CATEGORY_ID) return -1;
            if (b === ROOT_CATEGORY_ID) return 1;

            return (
                (categoriesById[a].orderNumber ?? 9999) -
                (categoriesById[b].orderNumber ?? 9999)
            );
        });
    }

    return {
        categoryId,
        categoriesById,
        categoryIds,
    };
}

function createQuantity(quantityDefinition, type, canEditPrice, normalizeQuantity) {
    return {
        ...normalizeQuantity(quantityDefinition),
        type,
        canEditPrice,
        nodeId: `q${quantityDefinition.id}`,
    };
}

export function ensureParent(quantitiesById, parent, normalizeParentQuantity) {
    const parentNodeId = `p${parent.id}`;

    let parentNode = quantitiesById[parentNodeId];

    if (!parentNode) {
        parentNode = {
            ...normalizeParentQuantity(parent),
            type: "parent",
            canEditPrice: true,
            quantityDefinitions: [],
        };

        quantitiesById[parentNodeId] = parentNode;
    }

    return parentNode;
}


export function registerQuantity(
    quantitiesById,
    quantityDefinition,
    type,
    canEditPrice,
    normalizeQuantity,
) {

    const quantity = createQuantity(
        quantityDefinition,
        type,
        canEditPrice,
        normalizeQuantity,
    );

    if (quantitiesById[quantity.nodeId]) {
        return null;
    }

    return {
        quantity,
        quantitiesById: {
            ...quantitiesById,
            [quantity.nodeId]: quantity,
        },
    };
}


export function upsertQuantity(quantitiesById, quantity) {
    const nodeId = quantity.nodeId;

    return {
        ...quantitiesById,
        [nodeId]: {
            ...(quantitiesById[nodeId] || {}),
            ...quantity,
            nodeId,
        },
    };
}

export function removeQuantityEverywhere(categoriesById, nodeId) {
    const updated = {};
    for (const [catId, category] of Object.entries(categoriesById)) {
        updated[catId] = {
            ...category,

            direct: (category.direct || []).filter(
                q => q.nodeId !== nodeId
            ),

            parents: (category.parents || []).map(parent => ({
                ...parent,
                quantityDefinitions: (parent.quantityDefinitions || []).filter(
                    q => q.nodeId !== nodeId
                ),
            })),
        };
    }

    return updated;
}


export function attachQuantityToTree(categoriesById, category, parent, quantity) {

    const categoryId = category.id == null ? ROOT_CATEGORY_ID : category.id;

    const categoryNode = categoriesById[categoryId];

    const updatedCategory = { ...categoryNode };
        const safeQuantity = {
            ...quantity,
            type: quantity.isDirect ? "direct" : "quantity",
            canEditPrice: quantity.isDirect ? true : false,
        };


    if (quantity.isDirect === true) {

        quantity.type = "direct";
        quantity.canEditPrice = true;
        updatedCategory.direct = [
            ...(updatedCategory.direct || []),
            safeQuantity,
        ];
    } else {
        quantity.type = "quantity";
        quantity.canEditPrice = false;
        updatedCategory.parents = (updatedCategory.parents || []).map(p => {
            if (p.id !== parent.id) return p;
            return {
                ...p,
                quantityDefinitions: [
                    ...(p.quantityDefinitions || []),
                    safeQuantity,
                ],
            };
        });
    }

    return {
        categoriesById: {
            ...categoriesById,
            [categoryId]: updatedCategory,
        },
    };
}


export function updateQuantityInTree(
    categoriesById,
    category,
    parent,
    quantity
) {
    const categoryId =
        category?.id == null
            ? ROOT_CATEGORY_ID
            : category.id;

    const currentCategory =
        categoriesById[categoryId];

    // category already deleted -> ignore event
    if (!currentCategory) {
        return categoriesById;
    }

    const safeQuantity = {
        ...quantity,
        type: quantity.isDirect
            ? "direct"
            : "quantity",

        canEditPrice:
            quantity.isDirect,
    };

    const updatedCategory = {
        ...currentCategory,
    };

    if (quantity.isDirect) {

        const index =
            updatedCategory.direct?.findIndex(
                q => q.nodeId === quantity.nodeId
            );

        if (index >= 0) {
            updatedCategory.direct =
                updatedCategory.direct.map(q =>
                    q.nodeId === quantity.nodeId
                        ? {
                              ...q,
                              ...safeQuantity,
                          }
                        : q
                );
        } else {
            updatedCategory.direct = [
                ...(updatedCategory.direct || []),
                safeQuantity,
            ];
        }

    } else {

        updatedCategory.parents =
            (updatedCategory.parents || []).map(p => {

                if (p.id !== parent?.id) {
                    return p;
                }

                const exists =
                    p.quantityDefinitions?.some(
                        q => q.nodeId === quantity.nodeId
                    );

                return {
                    ...p,

                    quantityDefinitions:
                        exists
                            ? p.quantityDefinitions.map(q =>
                                  q.nodeId === quantity.nodeId
                                      ? {
                                            ...q,
                                            ...safeQuantity,
                                        }
                                      : q
                              )
                            : [
                                  ...(p.quantityDefinitions || []),
                                  safeQuantity,
                              ],
                };
            });
    }

    return {
        ...categoriesById,
        [categoryId]: updatedCategory,
    };
}